import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import * as ContentListModal from '@/components/ContentListModal';
import ErrorState from '@/components/Error';
import SortAndFilter from '@/components/FilterManager/SortAndFilter';
import Loader from '@/components/Loader';
import AdminEmptyState from '@/modules/corporate/modules/admin/components/EmptyState';

import ContentListModalBody from './ContentListModalBody';
import { ContentsDialogProps, Items } from './types';
import useContentsDialog from './useContentsDialog';
import { DEFAULT_DIALOG_ACTIONS, getDefaultStrings } from './utils';

function ContentsDialog(props: ContentsDialogProps) {
  const { t } = useTranslation(['Admin', 'Events']);
  const {
    onClose,
    onAddItems,
    selectedIds,
    strings,
    dialogConfigs,
    dialogActions,
    TertiaryAction,
    disabledIds,
    variant,
  } = props;
  const {
    isAlreadySelected,
    isMultiAllowed,
    loading,
    shareable,
    isAssingmentTypeChangeAllowed,
    showDuration,
  } = Object.assign({}, DEFAULT_DIALOG_ACTIONS, dialogActions);

  const { selectedContentIds = [], selectedCollectionIds = [], eventSelections = [] } = selectedIds;

  const { title, description, primaryButtonText, secondaryButtonText, searchPlaceholder } =
    Object.assign({}, getDefaultStrings(t), strings);

  const {
    onAddClick,
    data,
    tabs,
    searchInput,
    setSearchStr,
    debouncedSearchStr,
    inViewTab,
    selectedCount,
    filterManager,
    sortAndFilterItems,
    childSessionsMap,
    getAssignmentType,
  } = useContentsDialog({
    t,
    onAddItems,
    isAlreadySelected,
    selectedIds,
    dialogConfigs,
    shareable,
  });

  const renderAdaptedContents = () => {
    if (data.error) {
      return <ErrorState className="h-full w-full flex-1 px-24" error={data.error} />;
    }

    if (data.adaptedContents?.length) {
      return (
        <ContentListModalBody
          childSessionsMap={childSessionsMap}
          data={data}
          disabledIds={disabledIds}
          eventSelections={eventSelections}
          getAssignmentType={getAssignmentType}
          inViewTab={inViewTab}
          isAlreadySelected={true}
          isAssingmentTypeChangeAllowed={isAssingmentTypeChangeAllowed}
          isMultiAllowed={isMultiAllowed}
          selectedCollectionIds={selectedCollectionIds}
          selectedContentIds={selectedContentIds}
          showDuration={showDuration}
        />
      );
    }

    if (data.isFetching) {
      return <Loader />;
    }

    return (
      <AdminEmptyState
        clearFilters={filterManager.methods.clearFilters}
        clearSearch={filterManager.methods.clearSearch}
        description={t('Admin:NoLearningContentDescription')}
        filter={filterManager.state.filter}
        search={searchInput}
        showIllustrationIcon={false}
        title={t('Admin:NoLearningContentAvailable')}
      />
    );
  };

  return (
    <ContentListModal.Main
      action={{
        discardText: secondaryButtonText,
        onSubmit: onAddClick,
        text: primaryButtonText,
        isSubmitting: loading,
        disabled: loading,
        TertiaryAction,
      }}
      fetchMore={data.fetchMore}
      header={{
        title: title,
        description: description,
      }}
      searchBar={{
        value: searchInput,
        onChange: setSearchStr,
        selectedCount: selectedCount,
        placeholder: searchPlaceholder,
      }}
      tabs={tabs}
      variant={variant}
      onClose={onClose}
      items={data.adaptedContents as Items[]}
      // moved out of ContentListModal because not every ContentListModal needs SortAndFilter
      sortComponent={
        sortAndFilterItems ? (
          <SortAndFilter
            className="mt-20 mb-12 px-8"
            filter={filterManager.state.filter}
            setFilter={filterManager.methods.setFilter}
            setSort={filterManager.methods.setSort}
            sort={filterManager.state.sort}
            sortAndFilterItems={sortAndFilterItems}
          />
        ) : undefined
      }>
      {renderAdaptedContents()}
    </ContentListModal.Main>
  );
}

export default ContentsDialog;
